/* @flow */
/**
 * External dependencies
 */
import * as React from 'react';
import { isFunction } from 'lodash';
// $FlowIssue
import { remove } from 'immutable';

/**
 * Internal dependencies
 */
import { canUseDOM } from '../../utils/canUseDom';

/**
 * Flow
 */
type apiState = {
	isLoading: boolean,
	error: any,
	success: any,
};

let inMemomyCache = {};

export default ( url: string | Object => string, key: string | Object => string, cachedData: Object | Object => Object = {} ): Function => ( WrappedComponent: React.ComponentType<any> ) : React.ComponentType<any> => {
	return class extends React.Component<{}, apiState> {
		isComponentMounted: boolean;
		prevUrl: string;

		constructor( props ) {
			super( props );

			// Make sure memory cache is cleared if SSR.
			if ( ! canUseDOM ) {
				inMemomyCache = {};
			}

			inMemomyCache = {
				...inMemomyCache,
				...this.getCache( props ),
			};

			const hasCache = ! ! inMemomyCache[ this.getKey() ];

			this.state = {
				isLoading: ! hasCache,
				success: hasCache ? true : undefined,
				error: undefined,
			};

			if ( hasCache ) {
				this.prevUrl = this.getUrl();
			}
		}

		componentDidMount() {
			this.isComponentMounted = true;
			this.fetchData( this.getUrl() );
		}

		componentWillUnmount() {
			this.isComponentMounted = false;
		}

		componentDidUpdate() {
			if ( this.urlHasChanged() ) {
				this.fetchData( this.getUrl() );
			}
		}

		urlHasChanged = () => {
			return this.prevUrl !== this.getUrl();
		};

		getUrl = (): string => {
			if ( isFunction( url ) ) {
				return url( this.props );
			}

			return url;
		}

		getKey = (): string => {
			if ( isFunction( key ) ) {
				return key( this.props );
			}

			return key;
		}

		getCache = ( props ): Object => {
			let cache = {};

			if ( isFunction( cachedData ) ) {
				cache = cachedData( props );
			} else {
				cache = cachedData;
			}

			if ( cache.init && 'intro' === cache.init[ 0 ].type ) {
				cache.intro = cache.init[ 0 ];
				cache.init = remove( cache.init, 0 );
			}

			return cache;
		}

		fetchData = ( resource: string ) => {
			if ( ! resource || ! this.urlHasChanged() ) {
				return;
			}

			this.prevUrl = resource;

			if ( ! ! inMemomyCache[ this.getKey() ] ) {
				this.setState( {
					isLoading: false,
					success: true,
					error: undefined,
				} );

				return;
			}

			// About to start fetching, set loading state
			this.setState( {
				isLoading: true,
				success: undefined,
				error: undefined,
			} );

			fetch( resource )
				.then( response => {
					if ( response.status === 200 ) {
						return response;
					}

					throw new Error( response.statusText );
				} )
				.then( response => response.json() )
				.then( data => {
					if ( ! this.isComponentMounted ) {
						return;
					}

					let intro = false;

					if ( 'intro' === data[ 0 ].type ) {
						intro = data[ 0 ];
						data = remove( data, 0 );
					}

					inMemomyCache = {
						...inMemomyCache,
						[ this.getKey() ]: data,
						intro: null,
					};

					if ( intro ) {
						inMemomyCache.intro = intro;
					}

					this.setState( {
						isLoading: false,
						success: true,
					} );
				} )
				.catch( error => {
					if ( ! this.isComponentMounted ) {
						return;
					}

					this.setState( {
						error,
						isLoading: false,
						success: false,
					} );
				} );
		}

		render() {
			return <WrappedComponent { ...this.props } { ...this.state } data={ inMemomyCache } />;
		}
	};
};



// WEBPACK FOOTER //
// ./src/components/higher-order/with-api-data.js